/**
 * IndexedDB storage provider — the default.
 *
 * Scales to millions of combinations and usage events with indexed lookups and
 * keeps everything client-side (no backend required). Uses `idb` for a promise
 * friendly wrapper. Object stores are keyed by domain id.
 */

import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type {
  Combination,
  ExtensionConfig,
  Favorite,
  UsageEvent,
} from "@/types";
import type { StorageProvider } from "./types";

interface SpcDb extends DBSchema {
  combinations: { key: string; value: Combination; indexes: { lastUsedAt: string; usageCount: number } };
  events: { key: string; value: UsageEvent; indexes: { combinationId: string; timestamp: string } };
  favorites: { key: string; value: Favorite };
  config: { key: string; value: ExtensionConfig & { _id: string } };
}

const DB_NAME = "smart-parameter-controller";
const DB_VERSION = 1;
const CONFIG_ID = "singleton";

export class IndexedDbProvider implements StorageProvider {
  readonly kind = "indexeddb";
  private db: IDBPDatabase<SpcDb> | null = null;

  async init(): Promise<void> {
    this.db = await openDB<SpcDb>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains("combinations")) {
          const store = db.createObjectStore("combinations", { keyPath: "id" });
          store.createIndex("lastUsedAt", "lastUsedAt");
          store.createIndex("usageCount", "usageCount");
        }
        if (!db.objectStoreNames.contains("events")) {
          const store = db.createObjectStore("events", { keyPath: "id" });
          store.createIndex("combinationId", "combinationId");
          store.createIndex("timestamp", "timestamp");
        }
        if (!db.objectStoreNames.contains("favorites")) {
          db.createObjectStore("favorites", { keyPath: "id" });
        }
        if (!db.objectStoreNames.contains("config")) {
          db.createObjectStore("config", { keyPath: "_id" });
        }
      },
    });
  }

  private get database(): IDBPDatabase<SpcDb> {
    if (!this.db) throw new Error("IndexedDbProvider used before init()");
    return this.db;
  }

  /** Close the underlying connection (e.g. when switching providers or in tests
   * so a subsequent deleteDatabase isn't blocked by an open handle). */
  close(): void {
    this.db?.close();
    this.db = null;
  }

  getCombinations(): Promise<Combination[]> {
    return this.database.getAll("combinations");
  }

  async putCombination(combination: Combination): Promise<void> {
    await this.database.put("combinations", combination);
  }

  async deleteCombination(id: string): Promise<void> {
    await this.database.delete("combinations", id);
  }

  getUsageEvents(): Promise<UsageEvent[]> {
    return this.database.getAll("events");
  }

  async appendUsageEvent(event: UsageEvent): Promise<void> {
    await this.database.put("events", event);
  }

  getFavorites(): Promise<Favorite[]> {
    return this.database.getAll("favorites");
  }

  async putFavorite(favorite: Favorite): Promise<void> {
    await this.database.put("favorites", favorite);
  }

  async deleteFavorite(id: string): Promise<void> {
    await this.database.delete("favorites", id);
  }

  async getConfig(): Promise<Partial<ExtensionConfig> | null> {
    const row = await this.database.get("config", CONFIG_ID);
    if (!row) return null;
    const { _id, ...config } = row;
    void _id;
    return config;
  }

  async putConfig(config: ExtensionConfig): Promise<void> {
    await this.database.put("config", { ...config, _id: CONFIG_ID });
  }

  async clearAll(): Promise<void> {
    const tx = this.database.transaction(
      ["combinations", "events", "favorites"],
      "readwrite",
    );
    await Promise.all([
      tx.objectStore("combinations").clear(),
      tx.objectStore("events").clear(),
      tx.objectStore("favorites").clear(),
    ]);
    await tx.done;
  }
}
